import React, { useState, useEffect } from "react";
import { ReactComponent as InfoIcon } from '../image/info.svg';
import { ReactComponent as WarningIcon } from '../image/warning.svg';
import { ReactComponent as CriticalIcon } from '../image/critical.svg';

const MaintenanceBanner = ({ bannerData, setbannerData ,isListView}) => {
  const [activeBanners, setActiveBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (bannerData) {
      const order = { critical: 0, warning: 1, info: 2 };

      setActiveBanners(
        [...bannerData].sort((a, b) => {
          const severityDiff =
            order[a.severityLevel.toLowerCase()] -
            order[b.severityLevel.toLowerCase()];
          // Different severities → sort by priority
          if (severityDiff !== 0) {
            return severityDiff;
          }

          // Same severity → newest startDateTime first
          return new Date(b.startDateTime) - new Date(a.startDateTime);
        })
      );
    }
  }, [bannerData]);

  const dateRegex = /([A-Z][a-z]+, \d{1,2} [A-Z][a-z]+ \d{4}, \d{1,2}:\d{2} [A-P]M – \d{1,2}:\d{2} [A-P]M [A-Z]{3})/;

  const severityConfigs = {
    info: {
      bgStyle: "bg-[#eff6ff] border-l-4 border-[#3b82f6]", 
      textColor: "text-[#1e40af]",
      iconBg: "bg-[#dbeafe] animate-[pulse-info_2s_ease-in-out_infinite]",
        tagBg: "bg-[#3b82f6]",
      highlight: "bg-[#3b82f6]/15 text-[#1d4ed8]",
      iconComponent: <InfoIcon className="w-5 h-5 text-[#3b82f6]" />
    },
    warning: {
      bgStyle: "bg-[#fffbeb] border-l-4 border-[#f59e0b]", 
      textColor: "text-[#78350f]",
      iconBg: "bg-[#fef3c7] animate-[pulse_2s_ease-in-out_infinite]", 
      tagBg: "bg-[#f59e0b]",
      highlight: "bg-[#f59e0b]/15 text-[#92400e]",
      iconComponent: <WarningIcon className="w-5 h-5 text-[#f59e0b]" />
    },
    critical: {
      bgStyle: "bg-[#fef2f2] border-l-4 border-[#d32f2f]", 
      textColor: "text-[#991b1b]",
      iconBg: "bg-[#fee2e2] animate-[pulse-critical_2s_ease-in-out_infinite]", 
      tagBg: "bg-[#d32f2f]",
      highlight: "bg-[#d32f2f]/15 text-[#b91c1c]",
      iconComponent: <CriticalIcon className="w-5 h-5 text-[#d32f2f]" />
    }
  };

  useEffect(() => {
    if (activeBanners.length <= 1) return;
    const interval = setInterval(() => {
      handleNext();
    }, 5000);
    return () => clearInterval(interval);
  }, [activeIndex, activeBanners.length]);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? activeBanners.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === activeBanners.length - 1 ? 0 : prev + 1));
  };

  const handleDismiss = (indexToRemove, e) => {
    e.stopPropagation();
    const updatedBanners = activeBanners.filter((_, idx) => idx !== indexToRemove);
    setActiveBanners(updatedBanners);

    if (activeIndex >= updatedBanners.length && updatedBanners.length > 0) {
      setActiveIndex(updatedBanners.length - 1);
    } else if (activeIndex === indexToRemove && indexToRemove === updatedBanners.length) {
      setActiveIndex(0);
      setbannerData([])
      sessionStorage.setItem('hasClosedBanner', 'true');
    }
  };

  if (activeBanners.length === 0) return null;

  const currentSeverity = activeBanners[activeIndex]?.severityLevel?.toLowerCase() || "info";
  const currentConfig = severityConfigs[currentSeverity];

  return (
    isListView ? (
      <div className="w-[320px] max-h-[500px] overflow-y-auto p-2 bg-[#e0ecfc] border border-gray-300 rounded-md shadow-lg">
        {activeBanners.map((item, index) => {
          const severity = item?.severityLevel?.toLowerCase() || "info";
          const config = severityConfigs[severity];
    
          return (
            <div
              key={item.bannerId || item.id || index}
              className={`mb-2 rounded-md p-3 ${config.bgStyle}`}
            >
              <div className="flex gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.iconBg}`}>
                  {config.iconComponent}
                </div>
    
                <div className="flex-1">
                  <div className={`font-semibold text-sm ${config.textColor}`}>
                    {item?.title}
                  </div>
    
                  <div className={`text-xs mt-1 ${config.textColor}`}>
                    {item?.message}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      ) 
      : 
    <div 
      className={`relative mx-5 mt-4 p-[14px] px-[20px] pb-[28px] border-l-[4px] rounded-[6px] shadow-[0_1px_3px_rgba(0,0,0,0.05)] transition-all duration-500 ease-out animate-[slideDown_0.4s_ease-out] ${currentConfig.bgStyle}`}
    >
      <div className="relative overflow-hidden px-8">
        {activeBanners.map((item, index) => {
          const severity = item?.severityLevel?.toLowerCase() || "info";
          const config = severityConfigs[severity];
          const parts = item?.message ? item.message.split(dateRegex) : [];
          const isActive = index === activeIndex;
  
          return (
            <div
              key={item.bannerId || item.id || index}
              className={`w-full flex items-center gap-[14px] transition-all duration-500 ease-in-out ${
                isActive 
                  ? "relative opacity-100 translate-x-0 z-10" 
                  : "absolute inset-x-0 top-0 opacity-0 translate-x-4 pointer-events-none z-0"
              }`}
            >
              {/* Circular Icon Frame */}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${config.iconBg}`}>
                {config.iconComponent}
              </div>
  
              {/* Message Content Area */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-[2px]">
                  <span className={`text-[14px] font-bold tracking-tight ${config.textColor}`}>
                    {item?.title}
                  </span>
                </div>
                
                <p className={`text-[13px] leading-[1.5] font-normal ${config.textColor}`}>
                  {parts.map((part, partIndex) =>
                    dateRegex.test(part) ? (
                      <span key={partIndex} className={`font-bold px-[6px] py-[1px] rounded-[3px] inline-block ${config.highlight}`}>
                        {part}
                      </span>
                    ) : (
                      part
                    )
                  )}
                </p>
              </div>
  
              {/* Close Button Action */}
              {item?.isDismissable !== false && (
                <button
                  type="button"
                  onClick={(e) => handleDismiss(index, e)}
                  className={`text-[20px] px-2 py-1 leading-none rounded-[4px] bg-transparent hover:bg-black/5 transition-colors ml-auto flex-shrink-0 self-start ${config.textColor}`}
                >
                  &times;
                </button>
              )}
            </div>
          );
        })}
      </div>
  
      {/* Slide Pagination Dots Indicator */}
      {activeBanners.length > 1 && (
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-20">
          {activeBanners.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === activeIndex ? "bg-gray-800 scale-110" : "bg-gray-400/40 hover:bg-gray-400"
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      )}
  
      {/* Control Chevrons */}
      {activeBanners.length > 1 && (
        <>
          <button
            type="button"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1 rounded-full bg-white/60 shadow-sm hover:bg-white text-gray-800 transition-all"
            onClick={handlePrev}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1 rounded-full bg-white/60 shadow-sm hover:bg-white text-gray-800 transition-all"
            onClick={handleNext}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

export default MaintenanceBanner;